import { create } from 'zustand';
import { Order, OrderStatus } from '../types';
import { ordersService, OrdersParams } from '../services/ordersService';

export interface OrdersState {
  orders: Order[];
  loading: boolean;
  error: string | null;
  filters: {
    status: OrderStatus;
    search: string;
    customerId?: string;
    dateFrom?: string;
    dateTo?: string;
    totalGte?: number;
    returned?: boolean;
    categoryId?: string;
    productIds?: string[];
    sourcePlatform?: 'grab' | 'shopee' | 'xanhsm' | 'internal';
  };
  pagination: {
    page: number;
    perPage: number;
    total: number;
  };
  sort: {
    field: string;
    order: 'ASC' | 'DESC';
  };
  // Actions
  fetchOrders: () => Promise<void>;
  updateOrderStatus: (id: string, status: OrderStatus) => Promise<void>;
  setFilters: (filters: Partial<OrdersState['filters']>) => void;
  clearFilters: () => void;
  setPagination: (pagination: Partial<OrdersState['pagination']>) => void;
  setSort: (sort: OrdersState['sort']) => void;
  clearError: () => void;
}

export const useOrdersStore = create<OrdersState>((set: any, get: any) => ({
  orders: [],
  loading: false,
  error: null,
  filters: {
    status: 'ordered',
    search: '',
  },
  pagination: {
    page: 1,
    perPage: 25,
    total: 0,
  },
  sort: {
    field: 'date',
    order: 'DESC',
  },

  fetchOrders: async () => {
    try {
      console.log('OrdersStore: Starting fetchOrders');
      set({ loading: true, error: null });
      const { filters, pagination, sort } = get();
      console.log('OrdersStore: Current state:', { filters, pagination, sort });

      const params: OrdersParams = {
        pagination: {
          page: pagination.page,
          perPage: pagination.perPage,
        },
        sort: {
          field: sort.field,
          order: sort.order,
        },
        filter: {
          ...filters,
          search: filters.search || undefined,
        },
      };
      console.log('OrdersStore: Calling ordersService with params:', params);
      console.log('OrdersStore: Filter object being sent:', params.filter);

      const result = await ordersService.getOrders(params);
      console.log('OrdersStore: Received result:', result);

      // SDK returns OrdersResponse with { data, total, page, perPage }
      const ordersData = result.data || [];
      const total = result.total || 0; // Use total from SDK response (from Content-Range header)

      console.log('OrdersStore: Result data length:', ordersData.length);
      console.log('OrdersStore: Result total:', total);

      set({
        orders: Array.isArray(ordersData) ? ordersData : [],
        pagination: { ...pagination, total },
        loading: false,
      });
      console.log(`✅ Loaded ${ordersData.length} orders`);
    } catch (error) {
      console.error('OrdersStore: Error fetching orders:', error);
      set({
        loading: false,
        error:
          error instanceof Error ? error.message : 'Failed to fetch orders',
      });
    }
  },

  updateOrderStatus: async (id: string, status: OrderStatus) => {
    try {
      let updatedOrder: Order;

      // Use special endpoint for delivered status to trigger inventory deduction
      if (status === 'delivered') {
        const response = await ordersService.markAsDelivered(id);
        updatedOrder = response as Order;
      } else {
        updatedOrder = (await ordersService.updateOrderStatus(
          id,
          status
        )) as Order;
      }

      const { orders } = get();
      const updatedOrders = orders.map((order: Order) =>
        order.id === id ? updatedOrder : order
      );

      set({ orders: updatedOrders });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Failed to update order status',
      });
    }
  },

  setFilters: (filters: Partial<OrdersState['filters']>) => {
    console.log('OrdersStore: Setting filters:', filters);
    console.log('OrdersStore: Current filters before update:', get().filters);
    set((state: OrdersState) => ({
      filters: { ...state.filters, ...filters },
      pagination: { ...state.pagination, page: 1 }, // Reset to first page when filters change
    }));
    console.log('OrdersStore: Filters after update:', get().filters);
    // Trigger a new API call after setting filters
    console.log('OrdersStore: Triggering fetchOrders after setting filters');
    get().fetchOrders();
  },

  clearFilters: () => {
    console.log('OrdersStore: Clearing all filters');
    console.log('OrdersStore: Current filters before clear:', get().filters);
    set((state: OrdersState) => ({
      filters: {
        status: state.filters.status,
        search: state.filters.search,
      },
      pagination: { ...state.pagination, page: 1 },
    }));
    console.log('OrdersStore: Filters after clear:', get().filters);
    // Trigger a new API call after clearing filters
    console.log('OrdersStore: Triggering fetchOrders after clearing filters');
    get().fetchOrders();
  },

  setPagination: (pagination: Partial<OrdersState['pagination']>) => {
    set((state: OrdersState) => ({
      pagination: { ...state.pagination, ...pagination },
    }));
  },

  setSort: (sort: OrdersState['sort']) => {
    set({ sort });
  },

  clearError: () => {
    set({ error: null });
  },
}));
